/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.20.0
 * @since 1.17.0
 */

import React, { Fragment, useEffect, useMemo } from 'react';

import { Formik, FormikHelpers } from 'formik';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import ButtonServiceUserDenied from '@/main/modules/role/components/ButtonServiceUserDenied';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import { LinkBlock } from '../../../components/common';
import { FormControlStyled } from '../../../components/common/FormStyled/FormStyled';
import FormFieldCheckbox from '../../../modules/form/components/FormFieldCheckbox';
import FormFieldInput from '../../../modules/form/components/FormFieldInput';
import FormFieldInputPassword from '../../../modules/form/components/FormFieldInputPassword';
import FormFieldInputPhone from '../../../modules/form/components/FormFieldInputPhone';
import FormFieldRadioButton from '../../../modules/form/components/FormFieldRadioButton';
import {
    makeSelectPageAutoReportIdTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation, IPageAutoReportIdTranslation } from '../../../modules/internationalization/types';
import { IRootReducer } from '../../../reducers/rootReducer';
import { autoReportIdGetTabMetaStartAction, autoReportIdUpdateFormatAction } from '../actions';
import { getAutoReportIdValidationSchema } from '../constants';
import { IAutoReportIdReducer } from '../reducer';
import { makeSelectorAutoReportIdFormatMeta } from '../selectors';
import {
    EAutoReportIdFormatField as F,
    EPageAutoReportIdInfo,
    IAutoReportIdFormatFields,
    IAutoReportIdGetTabMetaStartPayload,
} from '../types';
import { onSendByEmailFieldChange, onSendByEmailZipChange } from '../utils/autoReportIdFomatDeliveryUtil';
import { PageAutoReportIdFormatDeliveryStyled } from './styled/PageAutoReportIdFormatDeliveryStyled';


const AutoReportIdFormatDelivery = ({
                                        getTabMeta,
                                        formatMeta,
                                        isCdmAdditionalReportsEnabled,
                                        updateFormat,
                                        translationCommon,
                                        translationAutoReportId,
                                    }: IConnectedProps & IDispatchToProps) => {

    const validationSchema = useMemo(
        () => getAutoReportIdValidationSchema(translationCommon, isCdmAdditionalReportsEnabled),
        [isCdmAdditionalReportsEnabled, translationCommon]);
    const tFormatDelivery = useMemo(() => translationAutoReportId.formatDelivery, [translationAutoReportId]);

    const { reportFormats, ...fields } = formatMeta;

    useEffect(() => {
        getTabMeta({ tabId: EPageAutoReportIdInfo.FormatDelivery });
    }, [getTabMeta]);

    const onSubmit = (data: IAutoReportIdFormatFields, { setSubmitting }: FormikHelpers<IAutoReportIdFormatFields>) => {
        updateFormat(data);
        setSubmitting(false);
    };

    return (
        <PageAutoReportIdFormatDeliveryStyled>
            <Formik
                enableReinitialize
                initialValues={fields}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {({
                      dirty,
                      getFieldProps,
                      isSubmitting,
                      isValid,
                      resetForm,
                      setFieldValue,
                      setFieldTouched,
                      validateForm,
                      submitForm,
                  }) => (
                    <Fragment>
                        <div className="format-delivery__group">
                            <div className="format-delivery__title">
                                {tFormatDelivery.groups.format}
                            </div>
                            {reportFormats.map(format => (
                                <FormFieldRadioButton
                                    dataName={`report-format-${format}`}
                                    label={format}
                                    name={F.ReportFormat}
                                    value={format}
                                />
                            ))}
                        </div>

                        <div className="format-delivery__group">
                            <div className="format-delivery__title">
                                {tFormatDelivery.groups.notification}
                            </div>

                            {/* Об изменении расписания, на номер телефона */}
                            <FormFieldCheckbox
                                dataName="notify-by-phone-enable"
                                label={tFormatDelivery.fields[F.NotifyByPhoneEnabled]}
                                name={F.NotifyByPhoneEnabled}
                            />
                            {getFieldProps('notifyByPhoneEnabled').value && (
                                <div className="format-delivery__field">
                                    <FormFieldInputPhone
                                        dataName="notify-by-phone"
                                        label={tFormatDelivery.fields[F.NotifyByPhone]}
                                        mask={translationCommon.phoneInput.mask}
                                        name={F.NotifyByPhone}
                                    />
                                </div>
                            )}

                            {/* Об изменении расписания, на адрес электронной почты */}
                            <FormFieldCheckbox
                                dataName="notify-by-email-enable"
                                label={tFormatDelivery.fields[F.NotifyByEmailEnabled]}
                                name={F.NotifyByEmailEnabled}
                            />
                            {getFieldProps('notifyByEmailEnabled').value && (
                                <div className="format-delivery__field">
                                    <FormFieldInput
                                        dataName="notify-by-email"
                                        label={tFormatDelivery.fields[F.NotifyByEmail]}
                                        name={F.NotifyByEmail}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="format-delivery__group">
                            <div className="format-delivery__title">
                                {tFormatDelivery.groups.documentation}
                            </div>

                            {/* Отправить готовые документы на электронную почту */}
                            <FormFieldCheckbox
                                dataName="send-by-email-enable"
                                label={tFormatDelivery.fields[F.SendByEmailEnabled]}
                                name={F.SendByEmailEnabled}
                                onChange={() => {
                                    onSendByEmailFieldChange({ setFieldValue, setFieldTouched, validateForm });
                                }}
                            />
                            {getFieldProps('sendByEmailEnabled').value && (
                                <div className="format-delivery__field">
                                    <FormFieldInput
                                        dataName="send-by-email"
                                        label={tFormatDelivery.fields[F.SendByEmail]}
                                        name={F.SendByEmail}
                                    />
                                </div>
                            )}

                            { !isCdmAdditionalReportsEnabled &&
                                <Fragment>
                                    {/* Упаковать в архив (ZIP) */}
                                    <FormFieldCheckbox
                                        dataName="send-by-email-zip"
                                        disabled={!getFieldProps('sendByEmailEnabled').value}
                                        label={tFormatDelivery.fields[F.SendByEmailZip]}
                                        name={F.SendByEmailZip}
                                        onChange={() => {
                                            onSendByEmailZipChange({ setFieldValue, setFieldTouched, validateForm });
                                        }}
                                    />

                                    {/* Защитить паролем */}
                                    {getFieldProps('sendByEmailZip').value && (
                                        <Fragment>
                                            <FormFieldCheckbox
                                                dataName="send-by-email-zip-password-enabled"
                                                label={tFormatDelivery.fields[F.SendByEmailZipPasswordEnabled]}
                                                name={F.SendByEmailZipPasswordEnabled}
                                            />
                                            {getFieldProps('sendByEmailZipPasswordEnabled').value && (
                                                <div className="format-delivery__field">
                                                    <FormFieldInputPassword
                                                        dataName="send-by-email-zip-password"
                                                        label={tFormatDelivery.fields[F.SendByEmailZipPassword]}
                                                        name={F.SendByEmailZipPassword}
                                                    />
                                                </div>
                                            )}
                                        </Fragment>
                                    )}
                                </Fragment>
                            }

                        </div>


                        <FormControlStyled>
                            <ButtonServiceUserDenied
                                dataName="format-delivery-button-save"
                                disabled={!isValid || isSubmitting || !dirty}
                                kind="medium"
                                type="submit"
                                onClick={submitForm}
                            >
                                {translationCommon.form.save}
                            </ButtonServiceUserDenied>
                            <LinkBlock
                                dataName="cancelButton"
                                disabled={!dirty}
                                onClick={() => resetForm()}
                            >
                                {translationCommon.form.cancel}
                            </LinkBlock>
                        </FormControlStyled>
                    </Fragment>
                )}

            </Formik>
        </PageAutoReportIdFormatDeliveryStyled>
    );
};

interface IConnectedProps extends Pick<IAutoReportIdReducer, 'formatMeta'> {
    isCdmAdditionalReportsEnabled: boolean;
    translationAutoReportId: IPageAutoReportIdTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    formatMeta: makeSelectorAutoReportIdFormatMeta(),
    isCdmAdditionalReportsEnabled: makeSelectFeatureEnabled(FeatureNameEnum.CDM_ADDITIONAL_REPORTS),
    translationAutoReportId: makeSelectPageAutoReportIdTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

interface IDispatchToProps {
    getTabMeta: (payload: IAutoReportIdGetTabMetaStartPayload) => void;
    updateFormat: (payload: IAutoReportIdFormatFields) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchToProps => ({
    getTabMeta: payload => dispatch(autoReportIdGetTabMetaStartAction(payload)),
    updateFormat: payload => dispatch(autoReportIdUpdateFormatAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AutoReportIdFormatDelivery);
